import { useMemo, useState } from "react";

import { resultFileKey, type TransientResultFolder } from "../lib/formatJobResults";
import { groupResultScenarios } from "../lib/groupResultScenarios";
import { lastPathSegment } from "../lib/storedDirectory";
import { useCheckboxGroup } from "../schedules/hooks/useCheckboxGroup";

export const useResultSelection = (folders: TransientResultFolder[]) => {
  const grouped = useMemo(() => groupResultScenarios(folders), [folders]);
  const [activeKey, setActiveKey] = useState("");
  const scenarioNames = grouped.scenarios.map((scenario) => scenario.name);
  const resolvedKey = scenarioNames.includes(activeKey) ? activeKey : (scenarioNames[0] ?? "");
  const activeScenario = grouped.scenarios.find((scenario) => scenario.name === resolvedKey);
  const fileKeys = useMemo(
    () =>
      (activeScenario?.iterations ?? []).flatMap((iteration) =>
        iteration.files.map((file) => resultFileKey(iteration.folderName, file.name))
      ),
    [activeScenario]
  );
  const selection = useCheckboxGroup(fileKeys, []);
  const selected = selection.selected;
  const selectedFiles = useMemo(() => {
    if (!activeScenario) return [];
    return activeScenario.iterations.flatMap((iteration) =>
      iteration.files.flatMap((file) =>
        selected.has(resultFileKey(iteration.folderName, file.name))
          ? [{ fileName: lastPathSegment(file.name), folderName: iteration.folderName, path: file.path, url: file.url }]
          : []
      )
    );
  }, [activeScenario, selected]);

  return {
    activeKey: resolvedKey,
    activeScenario: activeScenario ?? null,
    isSelected: selection.isSelected,
    scenarios: grouped.scenarios,
    selectedFiles,
    setActiveKey,
    setMany: selection.setMany,
    toggle: selection.toggle,
  };
};
